import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import {
  ActivatedRoute,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationStart,
  Router,
  RouterOutlet,
  RouteReuseStrategy,
} from '@angular/router';
import { DomSanitizer, Title } from '@angular/platform-browser';
import { MatIconRegistry } from '@angular/material/icon';

import { FirebaseAuthService } from '@firebase/firebase-auth.service';
import { combineLatest } from 'rxjs';
import { filter, map, mergeMap, switchMap, take } from 'rxjs/operators';

import { RouterResolveStatusService } from '@shared/services/router-resolve-status.service';
import { ServerStatusService } from '@shared/services/server-status.service';
import { EnvironmentService } from '@shared/services/environment.service';
import { TubErrorReportingService } from '@shared/services/tub-error-reporting.service';
import { AuthenticationService } from './modules/authentication/authentication.service';
import { LazyInjectorService } from './modules/shared/services/lazy-injector.service';
import { TitleBadgeService } from '@app/modules/shared/services/title-badge.service';
import { CustomRouteReuseStrategy } from './classes/custom-route-reuse-strategy';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
  imports: [RouterOutlet],
})
export class AppComponent implements OnInit {
  private iconRegistry = inject(MatIconRegistry);
  private sanitizer = inject(DomSanitizer);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);
  private titleService = inject(Title);
  private firebaseAuth = inject(FirebaseAuthService);
  private authenticationService = inject(AuthenticationService);
  private routerResolveStatusService = inject(RouterResolveStatusService);
  private serverStatusService = inject(ServerStatusService);
  private environmentService = inject(EnvironmentService);
  private tubErrorReportingService = inject(TubErrorReportingService);
  private lazyInjectorService = inject(LazyInjectorService);
  private routeReuseStrategy = inject(RouteReuseStrategy);

  public readonly loading = signal(true);

  constructor() {
    this.initialiseCustomIcons();
    this.initialiseRouterEvents();

    // Clean up any existing null handles in the route reuse cache
    if (this.routeReuseStrategy instanceof CustomRouteReuseStrategy) {
      this.routeReuseStrategy.cleanupNullHandles();
    }
  }

  private baseTitle = 'Thrive Dashboard';

  ngOnInit(): void {
    const region =
      this.environmentService.getEnvironment().firebase.projectId === 'thrive-unified-backend-aus'
        ? ' - Australia'
        : ' - UK';
    this.baseTitle = this.environmentService.getEnvironment().dashboardTitle
      ? this.environmentService.getEnvironment().dashboardTitle + region
      : 'Thrive Dashboard' + region;

    this.updateTitleOnRouteChange();
    this.router.events.subscribe((event) => {
      this.navigationInterceptor(event);
    });
    this.serverStatusService.pollForServerStatus();
  }

  private navigationInterceptor(event): void {
    if (event instanceof NavigationStart) {
      // @ts-expect-error isLoading is not exposed as writable
      this.routerResolveStatusService.isLoading = true;
    } else if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
      // @ts-expect-error isLoading is not exposed as writable
      this.routerResolveStatusService.isLoading = false;
    } else if (event instanceof NavigationError) {
      // TODO: Handle this Navigation error when any of the API calls in the resolver fail
      this.tubErrorReportingService.send('Navigation failed', event.error, event);
    }
  }

  private updateTitleOnRouteChange(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let child = this.activatedRoute.firstChild;
          while (child) {
            if (child.firstChild) {
              child = child.firstChild;
            } else {
              return child.snapshot.data.title ? child.snapshot.data.title : null;
            }
          }
          return null;
        }),
        switchMap((pageTitle) => {
          const fullTitle = this.baseTitle + (pageTitle ? ` - ${pageTitle}` : '');
          this.titleService.setTitle(fullTitle);

          return combineLatest([
            this.firebaseAuth.user$,
            this.authenticationService.isScopePresent$('chat:view-active-chats'),
          ]).pipe(
            filter(([user, hasScope]) => !!user && hasScope),
            take(1),
            map(([user]) => ({ fullTitle, user }))
          );
        })
      )
      .subscribe(({ fullTitle }) => {
        void (async () => {
          const titleBadgeService = await this.lazyInjectorService.get<TitleBadgeService>(() =>
            import('./modules/shared/services/title-badge.service').then((m) => m.TitleBadgeService)
          );

          if (document.hasFocus()) {
            titleBadgeService.setBadge(fullTitle);
          } else {
            const listener = () => {
              titleBadgeService.setBadge(fullTitle);
              removeEventListener('focus', listener);
            };
            addEventListener('focus', listener);
          }
        })();
      });
  }

  private initialiseCustomIcons(): void {
    this.iconRegistry.setDefaultFontSetClass('material-symbols-rounded');
    //  Custom icons
    this.iconRegistry.addSvgIcon(
      'thrive',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/thrive.svg')
    );
    this.iconRegistry.addSvgIcon(
      'thrive-24',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/thrive-24.svg')
    );
    this.iconRegistry.addSvgIcon(
      'google-drive',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/google-drive.svg')
    );
    this.iconRegistry.addSvgIcon(
      'home',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/home.svg')
    );
    this.iconRegistry.addSvgIcon(
      'envelope',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/envelope.svg')
    );
    this.iconRegistry.addSvgIcon(
      'mail',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/mail.svg')
    );
    this.iconRegistry.addSvgIcon(
      'inbox',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/inbox.svg')
    );
    this.iconRegistry.addSvgIcon(
      'chat-settings',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/chat-settings.svg')
    );
    this.iconRegistry.addSvgIcon(
      'in-progress',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/in-progress.svg')
    );
    this.iconRegistry.addSvgIcon(
      'status-early',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/status-early.svg')
    );
    this.iconRegistry.addSvgIcon(
      'status-on-time',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/status-on-time.svg')
    );
    this.iconRegistry.addSvgIcon(
      'status-late',
      this.sanitizer.bypassSecurityTrustResourceUrl('/assets/images/svg/custom-icons/status-late.svg')
    );
  }

  private initialiseRouterEvents(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        })
      )
      .pipe(
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data)
      )
      .subscribe(() => {
        this.loading.set(false);
      });
  }
}
